import { defineStore } from 'pinia'
import { toast } from 'vue3-toastify'
import { fetchGalleryImages, uploadImage, bulkDeleteImages, updateImageTags } from '@/api/images'

const PER_PAGE = 40
const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20 mb
const SUPPORTED_TYPES = ['image/png', 'image/jpeg']

let abortController = null
let tempIdCounter = 0

/**
 * Gallery store — state for the organization media gallery page (/gallery).
 *
 * Populated by `Galleries.show` styxie via `setConfig(props)`.
 */
export const useGalleryStore = defineStore('gallery', {
  state: () => ({
    // Config from controller
    imageTags: [],
    categories: [],

    // Images list
    images: [],
    page: 0,
    allFiles: 0,
    imagesCount: null,
    order: 'desc',
    showMoreLoading: false,

    // Selection
    selectedIds: [],
    selectedTags: [],
    isShiftPressed: false,
    cursorIndex: 0,
    shiftCursorIndex: null,

    // Upload progress
    uploading: false,
    queuedFiles: 0,
    uploadedFiles: 0,
    totalLoading: 0
  }),
  getters: {
    isSelected: (state) => (id) => state.selectedIds.includes(id),
    hasMore: (state) => PER_PAGE * state.page < state.allFiles,
    selectedImageObjs: (state) => state.images.filter((image) => state.selectedIds.includes(image.id)),
    totalProgress: (state) => state.queuedFiles * 100
  },
  actions: {
    setConfig({ imageTags, categories }) {
      this.imageTags = imageTags || []
      this.categories = categories || []
    },
    async fetchImages() {
      abortController?.abort()
      abortController = new AbortController()
      this.showMoreLoading = true
      try {
        const res = await fetchGalleryImages({
          page: this.page + 1,
          per: PER_PAGE,
          taggedWith: this.selectedTags,
          order: this.order
        }, { signal: abortController.signal })
        this.applyFetchedPage(res)
      } catch (err) {
        if (err.code !== 'ERR_CANCELED') {
          this.showMoreLoading = false
          toast.error('Server error')
        }
      }
    },
    applyFetchedPage(res) {
      this.showMoreLoading = false
      this.page += 1
      if (this.page === 1) {
        this.selectAll(false)
        this.images = res.data
      } else {
        this.images = [...this.images, ...res.data]
      }
      this.allFiles = parseInt(res.headers['x-total-count'], 10)
      this.imagesCount = parseInt(res.headers['x-total-unscoped-count'], 10)
    },
    refetch() {
      this.page = 0
      this.fetchImages()
    },
    toggleTag(tag) {
      if (this.selectedTags.includes(tag)) {
        this.selectedTags.splice(this.selectedTags.indexOf(tag), 1)
      } else {
        this.selectedTags.push(tag)
      }
      this.refetch()
    },
    async unselectAllTags() {
      this.selectedTags = []
      this.page = 0
      await this.fetchImages()
    },
    setOrder(order) {
      this.order = order
      this.refetch()
    },
    setShiftPressed(val) {
      this.isShiftPressed = val
      if (!val) this.shiftCursorIndex = null
    },
    selectImage(id) {
      if (!id) return

      const imageIndex = this.images.findIndex((img) => img.id === id)
      if (this.isShiftPressed) {
        if (this.shiftCursorIndex !== null) {
          const start = Math.min(this.shiftCursorIndex, this.cursorIndex)
          const end = Math.max(this.shiftCursorIndex, this.cursorIndex)
          for (let i = start; i <= end; i++) {
            const idx = this.selectedIds.indexOf(this.images[i].id)
            if (idx !== -1) this.selectedIds.splice(idx, 1)
          }
        }

        this.shiftCursorIndex = imageIndex
        const start = Math.min(imageIndex, this.cursorIndex)
        const end = Math.max(imageIndex, this.cursorIndex)
        for (let i = start; i <= end; i++) {
          if (!this.selectedIds.includes(this.images[i].id)) {
            this.selectedIds.push(this.images[i].id)
          }
        }
      } else {
        this.cursorIndex = imageIndex
        const idx = this.selectedIds.indexOf(id)
        if (idx === -1) {
          this.selectedIds.push(id)
        } else {
          this.selectedIds.splice(idx, 1)
        }
      }
    },
    selectAll(flag) {
      if (flag) {
        this.images.forEach((image) => {
          if (!this.selectedIds.includes(image.id)) this.selectedIds.push(image.id)
        })
      } else {
        this.selectedIds = []
      }
    },
    async removeSelected() {
      try {
        const res = await bulkDeleteImages(this.selectedIds)
        this.images = this.images.filter((image) => !this.selectedIds.includes(image.id))
        this.selectedIds = []
        this.imagesCount = parseInt(res.headers['x-total-unscoped-count'], 10)
        if (this.imagesCount !== 0 && this.images.length === 0) this.refetch()
      } catch (err) {
        if (err?.response?.status === 403) return
        toast.error('Server error')
      }
    },
    async updateTags({ tagsToSave, tagsToRemove }) {
      try {
        await updateImageTags({ ids: this.selectedIds, tagsToSave, tagsToRemove })
        toast.success('Saving tags...')
      } catch (err) {
        if (err?.response?.status === 403) return
        toast.error("Server error. Can't update tags")
      }
    },
    // Called from the ImagesUpdateTagsChannel broadcast once the tags worker finishes
    applyUpdatedTags(tags) {
      this.imageTags = tags
      this.selectedTags = this.selectedTags.filter((tag) => tags.includes(tag))
      this.refetch()
    },
    addFiles(files) {
      const largeImages = []
      const filteredFiles = files.filter(({ name, size }) => {
        if (size > MAX_FILE_SIZE) {
          largeImages.push(name)
          return false
        }
        return true
      })

      if (largeImages.length) {
        toast.warning(`The image size exceeds 20mb, so you will not be able to upload it.\n${largeImages.join('\n')}`)
      }
      if (!filteredFiles.length) return

      const newImages = filteredFiles.map((file) => {
        tempIdCounter += 1
        const tempId = `uploading-${tempIdCounter}`
        if (!SUPPORTED_TYPES.includes(file.type)) {
          toast.error('Not supported file format')
          return { id: tempId, status: 'error', tags: [], error: { msg: 'Not supported file format' } }
        }

        const dataURL = URL.createObjectURL(file)
        this.uploadFile(file, tempId)
        return { id: tempId, dataURL, tags: []}
      })

      this.images = [...newImages, ...this.images]
    },
    async uploadFile(file, tempId) {
      this.queuedFiles += 1

      const config = {
        onUploadProgress: (progressEvent) => {
          const completed = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          const image = this.images.find((img) => img.id === tempId)
          if (!image) return
          image.status = 'uploading'
          this.totalLoading += completed - (image.progress || 0)
          image.progress = completed
          this.uploading = true
        }
      }

      const data = new FormData()
      data.append('file', file)

      try {
        const res = await uploadImage(data, config)
        const image = this.images.find((img) => img.id === tempId)
        if (image) {
          Object.assign(image, res.data, { status: 'success', progress: null })
        }
        this.uploadedFiles += 1
        this.imagesCount = this.imagesCount === null ? 1 : this.imagesCount + 1
        if (this.queuedFiles === this.uploadedFiles) {
          setTimeout(() => {
            this.uploading = false
            this.queuedFiles = 0
            this.uploadedFiles = 0
            this.totalLoading = 0
          }, 2000)
        }
      } catch {
        const image = this.images.find((img) => img.id === tempId)
        if (image) {
          image.status = 'error'
          image.progress = null
        }
        setTimeout(() => {
          this.uploading = false
        }, 2000)
        toast.error('Upload failed. Please upload a smaller file.')
      }
    }
  }
})
